import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import blogImg from "../assests/images/blog-bg.jpg";

import {
  FaArrowRight,
  FaCalendarAlt,
  FaSearch,
  FaTimes,
  FaCompass,
  FaRegNewspaper,
} from "react-icons/fa";

const API_URL = "https://cheapdubaihotels.com/api";

export default function Blogs() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");

  useEffect(() => {
    let isMounted = true;

    axios
      .get(`${API_URL}/blogs`)
      .then((res) => {
        if (!isMounted) return;

        const responseData = res.data.blogs || res.data;
        const data = Array.isArray(responseData) ? responseData : [];

        setBlogs(data);

        const uniqueCategories = [
          ...new Map(
            data
              .filter((blog) => blog.category?.id)
              .map((blog) => [blog.category.id, blog.category])
          ).values(),
        ];

        setCategories(uniqueCategories);
      })
      .catch((error) => {
        console.error("Unable to load blogs:", error);
        if (isMounted) setBlogs([]);
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const filteredBlogs = blogs.filter((blog) => {
    const keyword = search.trim().toLowerCase();

    const matchesSearch =
      !keyword ||
      blog.title?.toLowerCase().includes(keyword) ||
      blog.excerpt?.toLowerCase().includes(keyword) ||
      blog.description?.toLowerCase().includes(keyword);

    const matchesCategory =
      !selectedCategory ||
      String(blog.category?.id) === String(selectedCategory);

    return matchesSearch && matchesCategory;
  });

  const clearFilters = () => {
    setSearch("");
    setSelectedCategory("");
  };

  const formatDate = (date) => {
    if (!date) return "";

    const parsedDate = new Date(date);

    if (Number.isNaN(parsedDate.getTime())) return "";

    return parsedDate.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getImage = (blog) => {
    if (!blog.image) return blogImg;

    if (
      blog.image.startsWith("http://") ||
      blog.image.startsWith("https://")
    ) {
      return blog.image;
    }

    return `https://cheapdubaihotels.com/uploads/blogs/${blog.image}`;
  };

  const getDescription = (blog) =>
    blog.excerpt ||
    blog.description ||
    "Discover helpful travel tips, Dubai highlights, and ideas for your next stay.";

  const featuredBlog = filteredBlogs[0];
  const remainingBlogs = filteredBlogs.slice(1);

  return (
    <>
      <Helmet>
        <title>Dubai Travel Blog & Hotel Guides | Cheap Dubai Hotels</title>
        <meta
          name="description"
          content="Discover Dubai travel guides, hotel tips, destination inspiration and helpful advice for planning your next Dubai getaway."
        />
        <meta
          property="og:title"
          content="Dubai Travel Blog | Cheap Dubai Hotels"
        />
        <meta
          property="og:description"
          content="Explore Dubai hotel guides, local experiences and travel inspiration."
        />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="robots" content="index, follow" />
      </Helmet>

      {/* COMPACT HERO */}
      <section className="relative overflow-hidden bg-[#062F4F] mt-28">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-25"
          style={{ backgroundImage: `url(${blogImg})` }}
        />

        <div className="absolute inset-0 bg-gradient-to-r from-[#062F4F]/95 via-[#062F4F]/80 to-[#062F4F]/60" />

        <div className="relative mx-auto max-w-[1536px] px-5 py-10 sm:px-8 sm:py-12 lg:px-10 lg:py-14">
          <div className="max-w-2xl">
            <span className="mb-3 inline-flex items-center gap-2 rounded-full border border-[#F7B928]/40 bg-[#F7B928]/10 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.16em] text-[#F7B928]">
              <FaCompass />
              Dubai Travel Journal
            </span>

            <h1 className="text-3xl font-bold leading-tight text-white sm:text-4xl md:text-5xl">
              Explore Dubai.
              <span className="block text-[#F7B928]">
                Find Your Inspiration.
              </span>
            </h1>

            <p className="mt-3 max-w-xl text-sm leading-6 text-white/80 sm:text-base sm:leading-7">
              Discover travel tips, local experiences and hotel guides to help
              you plan a memorable Dubai getaway.
            </p>

            <div className="mt-5 flex flex-wrap items-center gap-4 text-xs font-medium text-white/80">
              <span className="flex items-center gap-2">
                <FaRegNewspaper className="text-[#F7B928]" />
                Travel Guides
              </span>
              <span className="h-1 w-1 rounded-full bg-[#F7B928]" />
              <span>Dubai Experiences</span>
              <span className="h-1 w-1 rounded-full bg-[#F7B928]" />
              <span>Hotel Tips</span>
            </div>
          </div>
        </div>
      </section>

      {/* BLOG CONTENT */}
      <main className="bg-[#F7FAFC]">
        <section className="relative w-full max-w-[1536px] mx-auto
              px-4
            sm:px-6
            lg:px-8
            py-20">
          {/* SECTION INTRO */}
          <div className="mb-7 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="mb-2 text-xs font-bold uppercase tracking-[0.18em] text-[#0B6E8E]">
                Ideas for your next trip
              </p>

              <h2 className="text-2xl font-bold tracking-tight text-[#062F4F] sm:text-3xl">
                The Dubai Travel Edit
              </h2>

              <p className="mt-2 max-w-xl text-sm leading-6 text-slate-500">
                Useful advice, destination highlights and inspiration for
                making the most of your stay.
              </p>
            </div>

            <div className="flex w-fit items-center gap-3 rounded-xl border border-[#DFE8ED] bg-white px-4 py-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#EAF5F8] text-[#0B6E8E]">
                <FaRegNewspaper className="text-lg" />
              </div>
              <div>
                <p className="text-lg font-bold leading-tight text-[#062F4F]">
                  {loading ? "—" : blogs.length}
                </p>
                <p className="mt-1 text-[11px] text-slate-500">
                  Travel articles
                </p>
              </div>
            </div>
          </div>

          {/* SEARCH AND FILTERS */}
          <div className="mb-9 rounded-2xl border border-[#DFE8ED] bg-white p-4 shadow-sm sm:p-5">
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-3 md:flex-row md:items-center">
                <div className="relative flex-1">
                  <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-sm text-slate-400" />

                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search articles, destinations or travel tips..."
                    aria-label="Search blog articles"
                    className="w-full rounded-xl border border-[#DFE8ED] bg-[#F7FAFC] py-3.5 pl-11 pr-4 text-sm text-[#062F4F] outline-none transition placeholder:text-slate-400 focus:border-[#0B6E8E] focus:bg-white focus:ring-4 focus:ring-[#0B6E8E]/10"
                  />
                </div>

                {(search || selectedCategory) && (
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="inline-flex items-center justify-center gap-2 rounded-xl border border-[#DFE8ED] px-4 py-3.5 text-sm font-semibold text-slate-600 transition hover:border-[#0B6E8E] hover:text-[#0B6E8E]"
                  >
                    <FaTimes />
                    Clear filters
                  </button>
                )}
              </div>

              {categories.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 border-t border-[#EDF2F5] pt-4">
                  <span className="mr-1 text-xs font-semibold text-slate-500">
                    Categories:
                  </span>

                  <button
                    type="button"
                    onClick={() => setSelectedCategory("")}
                    className={`rounded-lg px-3.5 py-2 text-xs font-semibold transition ${selectedCategory === ""
                      ? "bg-[#062F4F] text-white"
                      : "border border-[#DFE8ED] bg-white text-slate-600 hover:border-[#0B6E8E] hover:text-[#0B6E8E]"
                      }`}
                  >
                    All articles
                  </button>

                  {categories.map((category) => (
                    <button
                      type="button"
                      key={category.id}
                      onClick={() =>
                        setSelectedCategory(String(category.id))
                      }
                      className={`rounded-lg px-3.5 py-2 text-xs font-semibold transition ${String(selectedCategory) === String(category.id)
                        ? "bg-[#0B6E8E] text-white"
                        : "border border-[#DFE8ED] bg-white text-slate-600 hover:border-[#0B6E8E] hover:text-[#0B6E8E]"
                        }`}
                    >
                      {category.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* RESULTS HEADING */}
          <div className="mb-5 flex items-end justify-between gap-4">
            <div>
              <h3 className="text-xl font-bold text-[#062F4F] sm:text-2xl">
                {search || selectedCategory
                  ? "Matching Articles"
                  : "Latest Stories"}
              </h3>
              <p className="mt-1 text-xs text-slate-500 sm:text-sm">
                {search || selectedCategory
                  ? "Here is what we found for you."
                  : "Fresh ideas for your next Dubai adventure."}
              </p>
            </div>

            {!loading && (
              <span className="shrink-0 rounded-lg bg-[#EAF5F8] px-3 py-2 text-xs font-bold text-[#0B6E8E]">
                {filteredBlogs.length}{" "}
                {filteredBlogs.length === 1 ? "article" : "articles"}
              </span>
            )}
          </div>

          {/* LOADING SKELETONS */}
          {loading ? (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3, 4, 5, 6].map((item) => (
                <div
                  key={item}
                  className="overflow-hidden rounded-2xl border border-[#DFE8ED] bg-white"
                >
                  <div className="h-52 animate-pulse bg-slate-200" />
                  <div className="space-y-4 p-5">
                    <div className="h-3 w-24 animate-pulse rounded bg-slate-200" />
                    <div className="h-5 w-full animate-pulse rounded bg-slate-200" />
                    <div className="h-3 w-full animate-pulse rounded bg-slate-100" />
                    <div className="h-3 w-4/5 animate-pulse rounded bg-slate-100" />
                    <div className="h-9 w-28 animate-pulse rounded-lg bg-slate-100" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredBlogs.length > 0 ? (
            <>
              {/* FEATURED ARTICLE */}
              <Link
                to={`/blogs/${featuredBlog.slug}`}
                className="group mb-7 grid overflow-hidden rounded-2xl border border-[#DFE8ED] bg-white shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-xl lg:grid-cols-[1.15fr_1fr]"
              >
                <div className="relative min-h-[250px] overflow-hidden sm:min-h-[320px]">
                  <img
                    src={getImage(featuredBlog)}
                    alt={featuredBlog.title}
                    loading="eager"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = blogImg;
                    }}
                    className="absolute inset-0 h-full w-full object-cover transition duration-700 group-hover:scale-105"
                  />

                  <div className="absolute inset-0 bg-gradient-to-t from-[#062F4F]/70 via-transparent to-[#062F4F]/10" />

                  <span className="absolute left-5 top-5 rounded-lg bg-[#F7B928] px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-[#062F4F]">
                    Editor's Pick
                  </span>

                  <div className="absolute bottom-5 left-5 flex items-center gap-2 text-xs font-medium text-white">
                    <FaCalendarAlt className="text-[#F7B928]" />
                    {formatDate(featuredBlog.created_at)}
                  </div>
                </div>

                <div className="flex flex-col justify-center p-6 sm:p-8 lg:p-10">
                  <span className="mb-4 w-fit rounded-md bg-[#EAF5F8] px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-[#0B6E8E]">
                    {featuredBlog.category?.name || "Travel Guide"}
                  </span>

                  <h3 className="text-2xl font-bold leading-tight text-[#062F4F] transition group-hover:text-[#0B6E8E] sm:text-3xl">
                    {featuredBlog.title}
                  </h3>

                  <p className="mt-4 line-clamp-4 text-sm leading-7 text-slate-500">
                    {getDescription(featuredBlog)}
                  </p>

                  <div className="mt-7 flex items-center gap-3 text-sm font-bold text-[#FF5A1F]">
                    Read featured story
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#FFF0E9] transition group-hover:bg-[#FF5A1F] group-hover:text-white">
                      <FaArrowRight className="text-xs" />
                    </span>
                  </div>
                </div>
              </Link>

              {/* ARTICLE GRID */}
              {remainingBlogs.length > 0 && (
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                  {remainingBlogs.map((blog) => (
                    <Link
                      key={blog.id}
                      to={`/blogs/${blog.slug}`}
                      className="group flex h-full flex-col overflow-hidden rounded-2xl border border-[#DFE8ED] bg-white transition duration-300 hover:-translate-y-1 hover:border-[#B8D7E2] hover:shadow-xl"
                    >
                      <div className="relative h-52 overflow-hidden">
                        <img
                          src={getImage(blog)}
                          alt={blog.title}
                          loading="lazy"
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = blogImg;
                          }}
                          className="h-full w-full object-cover transition duration-700 group-hover:scale-105"
                        />

                        <div className="absolute inset-0 bg-gradient-to-t from-[#062F4F]/45 via-transparent to-transparent" />

                        <span className="absolute left-4 top-4 max-w-[85%] truncate rounded-md bg-white/95 px-3 py-1.5 text-[10px] font-bold text-[#0B6E8E] shadow-sm">
                          {blog.category?.name || "Travel Guide"}
                        </span>
                      </div>

                      <div className="flex flex-1 flex-col p-5 sm:p-6">
                        <div className="mb-3 flex items-center gap-2 text-xs text-slate-400">
                          <FaCalendarAlt className="text-[#0B6E8E]" />
                          {formatDate(blog.created_at)}
                        </div>

                        <h3 className="line-clamp-2 text-lg font-bold leading-snug text-[#062F4F] transition group-hover:text-[#0B6E8E]">
                          {blog.title}
                        </h3>

                        <p className="mt-3 line-clamp-3 text-sm leading-6 text-slate-500">
                          {getDescription(blog)}
                        </p>

                        <div className="mt-auto flex items-center justify-between border-t border-[#EDF2F5] pt-4 mt-5">
                          <span className="text-xs font-bold text-[#FF5A1F]">
                            Read article
                          </span>

                          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#EAF5F8] text-[#0B6E8E] transition group-hover:bg-[#FF5A1F] group-hover:text-white">
                            <FaArrowRight className="text-xs" />
                          </span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </>
          ) : (
            /* EMPTY STATE */
            <div className="rounded-2xl border border-[#DFE8ED] bg-white px-6 py-14 text-center sm:py-16">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[#EAF5F8] text-[#0B6E8E]">
                <FaSearch className="text-xl" />
              </div>

              <h3 className="mt-5 text-xl font-bold text-[#062F4F]">
                No articles found
              </h3>

              <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-slate-500">
                We couldn't find any articles matching your search. Try a
                different keyword or choose another category.
              </p>

              <button
                type="button"
                onClick={clearFilters}
                className="mt-6 inline-flex items-center gap-2 rounded-lg bg-[#FF5A1F] px-5 py-3 text-sm font-bold text-white transition hover:bg-[#e94b14]"
              >
                <FaTimes />
                Clear filters
              </button>
            </div>
          )}

          {/* BOTTOM CTA */}
          {!loading && filteredBlogs.length > 0 && (
            <section className="relative mt-12 overflow-hidden rounded-2xl bg-[#062F4F] px-6 py-9 sm:px-10 sm:py-11">
              <div className="absolute -right-12 -top-20 h-64 w-64 rounded-full border-[35px] border-white/5" />
              <div className="absolute -bottom-28 right-40 h-64 w-64 rounded-full bg-[#0B6E8E]/25 blur-3xl" />

              <div className="relative flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
                <div className="max-w-2xl">
                  <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#F7B928]">
                    Make your next trip count
                  </span>

                  <h3 className="mt-2 text-2xl font-bold leading-tight text-white sm:text-3xl">
                    Found your inspiration? Now find your Dubai stay.
                  </h3>

                  <p className="mt-3 text-sm leading-6 text-white/70">
                    Explore hotels and find an option that suits your travel
                    plans and budget.
                  </p>
                </div>

                <Link
                  to="/hotels"
                  className="inline-flex shrink-0 items-center justify-center gap-3 rounded-xl bg-[#FF5A1F] px-6 py-3.5 text-sm font-bold text-white transition hover:bg-white hover:text-[#062F4F]"
                >
                  Explore Hotels
                  <FaArrowRight />
                </Link>
              </div>
            </section>
          )}
        </section>
      </main>
    </>
  );
}